import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Upload, LogOut, Save, Eye, EyeOff, RefreshCw } from "lucide-react";
import { apiFetch } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "lw_admin_token";

const api = async (path, options = {}, retried = false) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  let body = options.body;
  if (options.json) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.json);
  }
  try {
    return await apiFetch(path, { method: options.method || "GET", headers, body, timeout: options.timeout });
  } catch (e) {
    if (e.status === 401 && !retried) {
      try {
        const rr = await fetch(`${API}/api/auth/refresh`, { method: "POST", credentials: "include" });
        if (rr.ok) {
          const d = await rr.json().catch(() => ({}));
          if (d.token) localStorage.setItem(TOKEN_KEY, d.token);
          return api(path, options, true);
        }
      } catch {}
      // Drop the dead token so the next page load shows the sign-in screen instead of a stuck dashboard
      localStorage.removeItem(TOKEN_KEY);
      throw new Error("Session expired, please sign in again");
    }
    throw e;
  }
};

const inputCls = "w-full rounded-sm border border-brand-border bg-white px-4 py-2.5 text-sm font-light text-brand-ink outline-none focus:border-brand-gold";
const labelCls = "mb-1.5 block text-[10px] font-medium uppercase tracking-[0.2em] text-brand-ink/50";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password: password.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm pt-44 pb-32 px-6" data-testid="admin-login">
      <img src="/assets/nandi.png" alt="Nandi" className="mx-auto h-16 w-auto" />
      <h1 className="mt-6 text-center font-display text-4xl font-medium text-brand-ink">Admin Sanctum</h1>
      <p className="mt-2 text-center text-sm font-light text-brand-ink/60">Only the keeper of the flame may enter.</p>
      <form onSubmit={submit} className="mt-10 space-y-5">
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} data-testid="admin-email-input" />
        </div>
        <div>
          <label className={labelCls}>Password</label>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className={`${inputCls} pr-12`}
              data-testid="admin-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              aria-label={showPw ? "Hide password" : "Show password"}
              data-testid="admin-password-toggle"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ink/40 hover:text-brand-maroon"
            >
              {showPw ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
            </button>
          </div>
        </div>
        {error && <p className="text-sm text-brand-maroon" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-button" className="w-full rounded-full bg-brand-maroon py-3.5 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory transition-colors duration-300 hover:bg-brand-gold disabled:opacity-50">
          {busy ? "Entering…" : "Enter"}
        </button>
      </form>
    </div>
  );
};

const ProductForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Photos up to 10 MB from a phone can take longer than the default 15s
      const data = await api("/api/admin/upload", { method: "POST", body: fd, timeout: 90000 });
      setForm({ ...form, img: data.url });
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (form.price != null && !(Number(form.price) > 0)) {
      alert("Price must be greater than 0.");
      return;
    }
    if (form.stock != null && (!Number.isInteger(Number(form.stock)) || Number(form.stock) < 0)) {
      alert("Stock must be a whole number, 0 or more.");
      return;
    }
    setBusy(true);
    try {
      if (form.id) {
        await api(`/api/admin/products/${form.id}`, { method: "PUT", json: form });
      } else {
        await api("/api/admin/products", { method: "POST", json: form });
      }
      onSaved();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-sm border border-brand-gold/50 bg-white p-6" data-testid="product-form">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="product-name-input" />
        </div>
        <div>
          <label className={labelCls}>Tag</label>
          <input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className={inputCls} data-testid="product-tag-input" />
        </div>
      </div>
      <div className="mt-4">
        <label className={labelCls}>Description</label>
        <textarea rows={3} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} className={inputCls} data-testid="product-desc-input" />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className={labelCls}>Order</label>
          <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className={inputCls} data-testid="product-order-input" />
        </div>
        <div>
          <label className={labelCls}>Price (₹, optional)</label>
          <input type="number" value={form.price ?? ""} onChange={(e) => setForm({ ...form, price: e.target.value === "" ? null : Number(e.target.value) })} className={inputCls} data-testid="product-price-input" />
        </div>
        <div>
          <label className={labelCls}>Stock (units, optional)</label>
          <input type="number" value={form.stock ?? ""} onChange={(e) => setForm({ ...form, stock: e.target.value === "" ? null : Number(e.target.value) })} className={inputCls} data-testid="product-stock-input" />
        </div>
      </div>
      <div className="mt-4">
        <label className={labelCls}>Image URL or upload</label>
        <div className="flex gap-2">
          <input value={form.img} onChange={(e) => setForm({ ...form, img: e.target.value })} className={inputCls} data-testid="product-img-input" />
          <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-brand-gold px-4 text-[10px] font-medium uppercase tracking-[0.15em] text-brand-maroon hover:bg-brand-gold hover:text-white" data-testid="product-upload-button">
            <Upload className="h-3.5 w-3.5" strokeWidth={1.5} />
            {uploading ? "…" : "Upload"}
            <input type="file" accept="image/*" onChange={upload} className="hidden" />
          </label>
        </div>
      </div>
      {form.img && <img src={form.img} alt="preview" className="mt-4 h-24 w-20 rounded-sm object-cover" data-testid="product-img-preview" />}
      <div className="mt-6 flex gap-3">
        <button onClick={save} disabled={busy || !form.name} data-testid="product-save-button" className="inline-flex items-center gap-2 rounded-full bg-brand-maroon px-6 py-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-ivory hover:bg-brand-gold disabled:opacity-50">
          <Save className="h-3.5 w-3.5" strokeWidth={1.5} /> {busy ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} data-testid="product-cancel-button" className="rounded-full border border-brand-border px-6 py-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-ink/60 hover:border-brand-gold">
          Cancel
        </button>
      </div>
    </div>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => fetch(`${API}/api/products`).then((r) => r.json()).then(setProducts);
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this offering?")) return;
    await api(`/api/admin/products/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl font-medium text-brand-ink">Offerings</h2>
        <button
          onClick={() => setEditing({ name: "", tag: "", desc: "", img: "", order: products.length + 1 })}
          data-testid="product-add-button"
          className="inline-flex items-center gap-2 rounded-full bg-brand-maroon px-5 py-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-ivory hover:bg-brand-gold"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add Product
        </button>
      </div>
      {editing && (
        <div className="mb-8">
          <ProductForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
        </div>
      )}
      <div className="space-y-4">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-5 rounded-sm border border-brand-border bg-white p-4" data-testid={`admin-product-${p.order}`}>
            {p.img && <img src={p.img} alt={p.name} className="h-16 w-14 rounded-sm object-cover" />}
            <div className="flex-1">
              <p className="font-display text-xl text-brand-ink">{p.order}. {p.name}</p>
              <p className="text-xs text-brand-ink/50">
                {p.tag}
                {p.price != null && ` · ₹${p.price}`}
                {p.stock != null && ` · ${p.stock} in stock`}
              </p>
            </div>
            <button onClick={() => setEditing(p)} data-testid={`product-edit-${p.order}`} className="rounded-full border border-brand-gold px-5 py-2 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-maroon hover:bg-brand-gold hover:text-white">
              Edit
            </button>
            <button onClick={() => remove(p.id)} data-testid={`product-delete-${p.order}`} aria-label="Delete product" className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-border text-brand-ink/50 hover:border-brand-maroon hover:text-brand-maroon">
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ContentTab = () => {
  const [content, setContent] = useState({});
  const [savedKey, setSavedKey] = useState("");

  useEffect(() => {
    fetch(`${API}/api/content`).then((r) => r.json()).then(setContent);
  }, []);

  const save = async (key) => {
    await api("/api/admin/content", { method: "PUT", json: { key, value: content[key] } });
    setSavedKey(key);
    setTimeout(() => setSavedKey(""), 2000);
  };

  return (
    <div data-testid="admin-content-tab">
      <h2 className="mb-2 font-display text-3xl font-medium text-brand-ink">Site Content</h2>
      <p className="mb-8 text-sm font-light text-brand-ink/60">Edit the words of the home page. Changes go live the moment you save.</p>
      <div className="space-y-6">
        {Object.entries(content).map(([key, value]) => (
          <div key={key} className="rounded-sm border border-brand-border bg-white p-5" data-testid={`content-field-${key}`}>
            <label className={labelCls}>{key}</label>
            <textarea rows={key.includes("subtext") || key.includes("note") ? 4 : 2} value={value} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className={inputCls} data-testid={`content-input-${key}`} />
            <button onClick={() => save(key)} data-testid={`content-save-${key}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-maroon px-5 py-2 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-ivory hover:bg-brand-gold">
              <Save className="h-3 w-3" strokeWidth={1.5} /> {savedKey === key ? "Saved ✓" : "Save"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChangePassword = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await api("/api/auth/change-password", { method: "POST", json: { current_password: current, new_password: next } });
      setMsg("Password changed successfully.");
      setCurrent("");
      setNext("");
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-sm space-y-5 rounded-sm border border-brand-border bg-white p-6" data-testid="change-password-form">
      <h2 className="font-display text-3xl font-medium text-brand-ink">Change password</h2>
      <div>
        <label className={labelCls}>Current password</label>
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className={inputCls} data-testid="change-current-input" />
      </div>
      <div>
        <label className={labelCls}>New password (min 10 characters)</label>
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={10} className={inputCls} data-testid="change-new-input" />
      </div>
      {msg && <p className="text-sm text-brand-maroon" data-testid="change-password-message">{msg}</p>}
      <button type="submit" disabled={busy} data-testid="change-password-button" className="rounded-full bg-brand-maroon px-8 py-3 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory transition-colors duration-300 hover:bg-brand-gold disabled:opacity-50">
        {busy ? "Saving…" : "Change Password"}
      </button>
    </form>
  );
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const STATUS_STYLE = {
  paid: "bg-[#2F6B3A] text-white",
  created: "border border-brand-gold bg-brand-gold/15 text-brand-maroon",
  expired: "border border-brand-ink/20 bg-brand-ink/5 text-brand-ink/60",
  failed: "border border-brand-maroon/40 bg-brand-maroon/10 text-brand-maroon",
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [checking, setChecking] = useState("");

  const load = useCallback(
    () =>
      api(`/api/admin/orders${status ? `?status=${status}` : ""}`)
        .then((rows) => {
          setOrders(rows);
          setError("");
        })
        .catch((e) => setError(e.message)),
    [status]
  );

  useEffect(() => {
    load();
  }, [load]);

  const checkPayment = async (orderId) => {
    setChecking(orderId);
    setError("");
    setNotice("");
    try {
      const r = await api(`/api/admin/orders/${orderId}/check-payment`, { method: "POST" });
      setNotice(
        r.status === "paid"
          ? `Order ${orderId} is confirmed paid — stock and the owner email have been updated.`
          : `Order ${orderId} is still unpaid at Razorpay.`
      );
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setChecking("");
    }
  };

  return (
    <div data-testid="admin-orders-tab">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-3xl font-medium text-brand-ink">Orders</h2>
        <div className="flex items-center gap-3">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter" className="rounded-sm border border-brand-border bg-white px-4 py-2 text-sm font-light outline-none focus:border-brand-gold">
            <option value="">All statuses</option>
            <option value="created">Created</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="expired">Expired</option>
          </select>
          <button onClick={load} data-testid="orders-refresh-button" aria-label="Refresh orders" className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-border text-brand-ink/60 hover:border-brand-gold">
            <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {error && <p className="mb-4 text-sm text-brand-maroon" data-testid="orders-error">{error}</p>}
      {notice && <p className="mb-4 text-sm text-[#2F6B3A]" data-testid="orders-check-notice">{notice}</p>}
      {orders.length === 0 ? (
        <p className="rounded-sm border border-dashed border-brand-border bg-white px-6 py-14 text-center font-display text-xl italic text-brand-ink/50" data-testid="orders-empty">
          No orders yet — they will appear here the moment a payment begins.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-brand-border bg-white">
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="orders-table">
            <thead>
              <tr className="border-b border-brand-border text-[10px] uppercase tracking-[0.2em] text-brand-ink/50">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} className="border-b border-brand-border/60 last:border-0" data-testid={`order-row-${o.order_id}`}>
                  <td className="px-4 py-3 font-light text-brand-ink/70">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3">{o.customer_name || "—"}</td>
                  <td className="px-4 py-3 font-light text-brand-ink/70">{o.customer_contact || "—"}</td>
                  <td className="px-4 py-3 font-light text-brand-ink/70">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                  <td className="px-4 py-3 font-medium text-brand-maroon">₹{((o.amount || 0) / 100).toFixed(0)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] ${STATUS_STYLE[o.status] || STATUS_STYLE.created}`} data-testid={`order-status-${o.order_id}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {o.status === "paid" && !o.email_sent && (
                        <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-brand-maroon" data-testid={`order-email-warning-${o.order_id}`}>
                          email not sent ({o.email_attempts} tries)
                        </span>
                      )}
                      {o.stock_issue && (
                        <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-brand-maroon" data-testid={`order-stock-warning-${o.order_id}`}>
                          stock issue
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {o.status === "created" && (
                      <button
                        onClick={() => checkPayment(o.order_id)}
                        disabled={checking === o.order_id}
                        data-testid={`order-check-${o.order_id}`}
                        className="rounded-full border border-brand-gold px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.15em] text-brand-maroon hover:bg-brand-gold hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {checking === o.order_id ? "Checking…" : "Check payment"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("products");

  if (!authed) {
    return (
      <main className="min-h-screen">
        <Login onLogin={() => setAuthed(true)} />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-6 pt-36 pb-24 lg:px-12" data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold">Admin Sanctum</p>
            <h1 className="mt-2 font-display text-4xl font-medium text-brand-ink">Tend the flame</h1>
          </div>
          <button
            onClick={() => { localStorage.removeItem(TOKEN_KEY); window.location.reload(); }}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-brand-border px-5 py-2 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-ink/60 hover:border-brand-maroon hover:text-brand-maroon"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Logout
          </button>
        </div>
        <div className="mt-10 flex gap-3 border-b border-brand-border pb-4">
          {["products", "orders", "content", "account"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`rounded-full px-6 py-2.5 text-[10px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${tab === t ? "bg-brand-maroon text-brand-ivory" : "border border-brand-border text-brand-ink/60 hover:border-brand-gold"}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="mt-8">{tab === "products" ? <ProductsTab /> : tab === "orders" ? <OrdersTab /> : tab === "content" ? <ContentTab /> : <ChangePassword />}</div>
      </motion.div>
    </main>
  );
}
